import type { ApiFetchError } from '@/hooks/api-fetcher'
import { useApiErrorToastStore } from '@/stores/api-error-toast-store'
import { useBillingDialogStore } from '@/stores/billing-dialog-store'
import { traceFromDetails } from '@/lib/trace-context'

interface ApiErrorResponse {
  success: false
  error: {
    code: string
    message: string
    details?: unknown
  }
}

interface ApiErrorObject {
  message: string
  code?: string
  details?: unknown
}

/**
 * Handle API error responses, especially 500 errors.
 * Logs to console.error and shows toast notification.
 */
export function handleApiError(
  error: unknown,
  url?: string,
  statusCode?: number,
): void {
  let errorMessage = 'An error occurred'
  let errorCode: string | undefined
  let errorDetails: unknown
  let traceId: string | undefined
  let requestId: string | undefined

  // Extract error information from different error formats
  if (error instanceof Error && 'statusCode' in error) {
    // ApiFetchError instance
    const apiError = error as ApiFetchError
    errorMessage = apiError.message
    errorCode = apiError.code
    errorDetails = apiError.details
    traceId = apiError.traceId
    requestId = apiError.requestId
    statusCode = apiError.statusCode
    url = apiError.url
  } else if (error instanceof Error) {
    // Generic Error instance
    errorMessage = error.message
  } else if (typeof error === 'object' && error !== null && 'error' in error) {
    // ApiErrorResponse format
    const apiError = error as ApiErrorResponse
    errorMessage = apiError.error.message
    errorCode = apiError.error.code
    errorDetails = apiError.error.details
  } else if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error
  ) {
    // ApiErrorObject format
    const apiError = error as ApiErrorObject
    errorMessage = apiError.message
    errorCode = apiError.code
    errorDetails = apiError.details
  } else if (typeof error === 'object' && error !== null) {
    // Unknown object format - try to extract any useful info
    const anyError = error as Record<string, unknown>
    errorMessage = String(
      anyError.message || anyError.msg || 'An error occurred',
    )
    errorCode = String(anyError.code || anyError.error_code || 'unknown')
    errorDetails = anyError.details
  }

  if (!traceId || !requestId) {
    const detailTrace = traceFromDetails(errorDetails)
    traceId = traceId ?? detailTrace.traceId
    requestId = requestId ?? detailTrace.requestId
  }

  // Log to console.error with full context
  console.error('[API Error]', {
    url,
    statusCode,
    code: errorCode,
    message: errorMessage,
    traceId,
    requestId,
    details: errorDetails,
    error,
  })

  // Handle 402 — open pricing dialog
  if (statusCode === 402 || errorCode === 'insufficient_credits') {
    useBillingDialogStore.getState().open('exhausted')
    return
  }

  // Show a toast only for genuine server faults (500). A 404 is "not
  // found" — an expected state when a resource was deleted or never
  // existed (e.g. a background SWR revalidation of an install the user
  // just disconnected). Surfacing it as a global "Server Error" toast is
  // both wrong and noisy (SWR retries multiply it into a stack). The
  // error is still logged above and returned to the caller via the hook's
  // `error`, so components can render their own empty / not-found state.
  if (statusCode === 500) {
    // Only the trace id crosses into the UI. `errorMessage` is a diagnostic —
    // it is logged above and must not be shown; an earlier version passed it
    // through when `isUserSafeErrorText` accepted it, which it does for text
    // like "create failed (500): psycopg2.OperationalError: ...", because a
    // diagnostic and a curated sentence are indistinguishable by shape. The
    // copy itself lives in `<ApiErrorToast />`, which has a translator.
    useApiErrorToastStore.getState().report(traceId)
  }
}
